/**
 * Datamodellen för det digitala rabatthäftet.
 *
 * Den här filen är enda sanningen om hur datan ser ut och används av både
 * webbappen och Cloud Functions. Funktionsmappen får en kopia vid bygge
 * (scripts/sync-shared.mjs) eftersom Firebase bara laddar upp functions/.
 */

export const COLLECTIONS = {
  users: 'users',
  teams: 'teams',
  booklets: 'booklets',
  codes: 'codes',
  companies: 'companies',
  offers: 'offers',
  redemptions: 'redemptions',
  auditLog: 'auditLog',
  reports: 'reports',
  stats: 'stats',
  codeBatches: 'codeBatches',
  orders: 'orders',
  statsDaily: 'statsDaily',
  statsActivation: 'statsActivation',
  publicSummary: 'publicSummary',
} as const

/**
 * Det enda som får läsas utan inloggning.
 *
 * Fyra tal som säger vad häftet är värt, så att inloggningssidan kan svara på
 * frågan "vad får jag för pengarna?" innan man skapat ett konto. Erbjudandena
 * själva är varan och ligger kvar bakom inloggning – lägg aldrig till
 * företagsnamn eller rabattvillkor här.
 *
 * Skrivs bara av servern, se functions/src/publicSummary.ts.
 */
export interface PublicSummary {
  bookletId: string | null
  periodName: string | null
  offerCount: number
  /** Summan av erbjudandenas schablonvärden, i kronor. */
  totalValueSek: number
  priceSek: number
}

/**
 * Tidsstämplar. Klientens och serverns Timestamp-klasser är olika typer men
 * har samma yta – det här är den delmängd vi faktiskt använder.
 */
export interface TimestampLike {
  toDate(): Date
  toMillis(): number
}

/* ------------------------------------------------------------------ *
 * Roller
 * ------------------------------------------------------------------ */

export type Role = 'buyer' | 'teamLeader' | 'admin' | 'superadmin'

export const ROLE_LABELS: Record<Role, string> = {
  buyer: 'Köpare',
  teamLeader: 'Lagledare',
  admin: 'Administratör',
  superadmin: 'Superadministratör',
}

/** Högre siffra = fler rättigheter. Används för enkla behörighetskontroller. */
export const ROLE_RANK: Record<Role, number> = {
  buyer: 0,
  teamLeader: 1,
  admin: 2,
  superadmin: 3,
}

export function hasAtLeastRole(role: Role | undefined, required: Role): boolean {
  if (!role) return false
  return ROLE_RANK[role] >= ROLE_RANK[required]
}

/* ------------------------------------------------------------------ *
 * Kategorier
 * ------------------------------------------------------------------ */

export const CATEGORIES = [
  'restaurang',
  'mat',
  'sport',
  'skonhet',
  'butik',
  'bil',
  'noje',
  'tjanster',
] as const

export type Category = (typeof CATEGORIES)[number]

export const CATEGORY_LABELS: Record<Category, string> = {
  restaurang: 'Restaurang & café',
  mat: 'Mat & dryck',
  sport: 'Sport & fritid',
  skonhet: 'Skönhet & hälsa',
  butik: 'Butik & shopping',
  bil: 'Bil & service',
  noje: 'Nöje & upplevelser',
  tjanster: 'Tjänster',
}

/* ------------------------------------------------------------------ *
 * Häften (perioder)
 * ------------------------------------------------------------------ */

export type BookletStatus = 'upcoming' | 'active' | 'expired'

export const BOOKLET_STATUS_LABELS: Record<BookletStatus, string> = {
  upcoming: 'Kommande',
  active: 'Aktivt',
  expired: 'Utgånget',
}

/** Standardpris per häfte. Ändras per period i admin. */
export const DEFAULT_PRICE_SEK = 220

/** Föreningens årsmål – 4 000 häften, dvs. 2 000 per period. */
export const DEFAULT_PERIOD_GOAL = 2000

/**
 * Hur många häften varje spelare förväntas sälja när inget annat är satt.
 *
 * Föreningen sätter siffran per lag utifrån ålder – de äldsta lagen säljer
 * fler än de yngsta – men ett nytt lag behöver ett rimligt utgångsvärde.
 */
export const DEFAULT_BOOKLETS_PER_PLAYER = 4

export interface Booklet {
  id: string
  /** Visningsnamn, t.ex. "Januari–juni 2027". */
  periodName: string
  /** ISO-datum (YYYY-MM-DD) i svensk tid. Lagras som sträng för att undvika
   *  tidszonsförvirring – ett häfte gäller kalenderdagar, inte klockslag. */
  startDate: string
  endDate: string
  status: BookletStatus
  priceSek: number
  salesGoal: number
  /** Antal dagar före slutdatum som påminnelsen börjar visas. */
  reminderDaysBefore: number
  /** Trösklar i kronor för sparar-nivåerna. */
  savingsTiers: SavingsTiers
  /** Testhäften visas med TEST-märkning och räknas inte in i statistiken. */
  isTest: boolean
  createdAt: TimestampLike
  createdBy: string
  updatedAt?: TimestampLike
}

export interface SavingsTiers {
  /** Nivå 1 – häftet har betalat sig. Sätts normalt till priset. */
  small: number
  medium: number
  large: number
}

export function defaultSavingsTiers(priceSek: number): SavingsTiers {
  return { small: priceSek, medium: priceSek * 3, large: priceSek * 6 }
}

export type SavingsTier = 'none' | 'small' | 'medium' | 'large'

export const SAVINGS_TIER_LABELS: Record<SavingsTier, string> = {
  none: 'Nystartad',
  small: 'Småsparare',
  medium: 'Mellansparare',
  large: 'Storsparare',
}

export function savingsTierFor(totalSek: number, tiers: SavingsTiers): SavingsTier {
  if (totalSek >= tiers.large) return 'large'
  if (totalSek >= tiers.medium) return 'medium'
  if (totalSek >= tiers.small) return 'small'
  return 'none'
}

/* ------------------------------------------------------------------ *
 * Lag
 * ------------------------------------------------------------------ */

export interface Team {
  id: string
  name: string
  /** Antal spelare – styr hur många koder som genereras i en batch. */
  playerCount: number
  /**
   * Häften per spelare, satt av föreningen utifrån lagets ålder.
   *
   * Tillsammans med antalet spelare ger den lagets åtagande, som styr både
   * hur många koder som föreslås och hur många en förälder får beställa.
   *
   * Valfritt i typen för att lag som lades upp innan fältet fanns ska gå att
   * läsa – använd DEFAULT_BOOKLETS_PER_PLAYER när det saknas.
   */
  bookletsPerPlayer?: number
  /**
   * Lagets Swish-nummer, dit beställningar betalas.
   *
   * Sitter på laget och inte på föreningen därför att varje lag redan har en
   * egen swishkod idag och pengarna går till lagets kassa. Att flytta dem
   * till ett gemensamt konto vore en ekonomisk förändring, inte en teknisk,
   * och den ska inte smygas in via en programändring.
   */
  swishNumber?: string | null
  /**
   * Betallänk, om laget tar emot betalningar via Digitala Lagkassan eller
   * något annat system i stället för ett rått Swish-nummer.
   *
   * Finns båda vinner länken. Skälet är att en länk pekar på ett system som
   * kan bokföra betalningen där lagkassan faktiskt finns, medan ett Swish-
   * nummer bara flyttar pengar – och då måste någon föra över dem för hand.
   */
  paymentUrl?: string | null
  /** Lagledarnas uid. Ett lag kan ha flera ledare. */
  leaderUids: string[]
  active: boolean
  /** Skapat av "Skapa testhäfte" och raderas av "Rensa testdata". */
  isTest?: boolean
  createdAt: TimestampLike
  updatedAt?: TimestampLike
}

/**
 * Lagets åtagande: hur många häften laget förväntas sälja under en period.
 *
 * Ligger som funktion i stället för utspridda `?? 4` runt om i koden. Ett
 * standardvärde som upprepas på fem ställen slutar förr eller senare vara
 * samma värde på alla fem.
 */
export function teamCommitment(
  team: Pick<Team, 'playerCount' | 'bookletsPerPlayer'>,
): number {
  return team.playerCount * (team.bookletsPerPlayer ?? DEFAULT_BOOKLETS_PER_PLAYER)
}

/** Räknare per lag och häfte, underhålls av Cloud Functions. */
export interface TeamBookletStats {
  id: string // `${teamId}__${bookletId}`
  teamId: string
  bookletId: string
  codesTotal: number
  codesSold: number
  codesActivated: number
  codesPaid: number
  updatedAt: TimestampLike
}

/* ------------------------------------------------------------------ *
 * Koder
 * ------------------------------------------------------------------ */

export type CodeStatus = 'unsold' | 'sold' | 'activated'

export const CODE_STATUS_LABELS: Record<CodeStatus, string> = {
  unsold: 'Osåld',
  sold: 'Såld',
  activated: 'Aktiverad',
}

export interface Code {
  /** Dokument-id är själva koden i normaliserad form, t.ex. "A3K9M2XP". */
  id: string
  bookletId: string
  teamId: string
  batchId: string
  status: CodeStatus
  /** Sätts av lagledaren efter avstämning mot Digitala Lagkassan. */
  paid: boolean
  paidAt?: TimestampLike | null
  paidBy?: string | null
  /** Fylls i vid digitalt säljflöde. */
  soldToEmail?: string | null
  /** Valfritt: vilken spelare som sålde. Ger säljstatistik på spelarnivå. */
  soldByPlayerName?: string | null
  soldAt?: TimestampLike | null
  soldBy?: string | null
  /**
   * Föräldern som köpt häftet och ska sälja det vidare.
   *
   * Skiljer sig från ownerUid, som är den som till slut aktiverar och
   * använder häftet. En kod kan alltså ha en hållare men ingen ägare – det
   * är precis läget mellan att föräldern betalat och att köparen skrapat.
   */
  holderUid?: string | null
  /** Beställningen koden delades ut i, om den kom den vägen. */
  orderRef?: string | null
  /** Sätts när föräldern lämnat koden vidare till en köpare. */
  handedOutAt?: TimestampLike | null
  handedOutTo?: string | null

  /** Sätts när köparen aktiverar koden. */
  ownerUid?: string | null
  activatedAt?: TimestampLike | null
  isTest: boolean
  createdAt: TimestampLike
  createdBy: string
}

export interface CodeBatch {
  id: string
  bookletId: string
  teamId: string
  count: number
  createdAt: TimestampLike
  createdBy: string
  note?: string
  isTest: boolean
}

/* ------------------------------------------------------------------ *
 * Företag och erbjudanden
 * ------------------------------------------------------------------ */

import type { OfferTerms, OfferType } from './offers'

export interface Company {
  id: string
  name: string
  category: Category
  logoUrl?: string | null
  /**
   * Företagets profilfärg som hex, t.ex. "#c2410c".
   *
   * Används sparsamt och med flit: som smal remsa i erbjudanderaden, som
   * omslag på företagssidan och som förvald bakgrund på företagets kuponger.
   * Den fyller aldrig en listrad – tolv företag som var för sig skriker gör
   * en lista där ingenting läses, vilket är samma skäl som gör att
   * företagsavataren är dämpad.
   */
  brandColor?: string | null
  /**
   * Företagets grafiska profil – hur dess erbjudanden ser ut i häftet.
   *
   * Sätts en gång på företaget och gäller sedan alla dess erbjudanden som
   * inte fått en egen design. Utan den fick varje erbjudande ställas in för
   * sig, och ett företag med fyra kuponger såg ut som fyra olika företag
   * så fort någon glömde ett val.
   *
   * Rubriken används aldrig här – den är erbjudandets text, inte företagets
   * utseende. Saknas profilen gäller företagets färg, och annars Lira.
   */
  pageStyle?: OfferPageStyle | null
  /** Bred bild överst på företagssidan. Gör företaget till en plats. */
  coverImageUrl?: string | null
  /** Fritext, t.ex. "Mån–fre 10–18, lör 10–15". Strukturerade tider vore
   *  mer än ett rabatthäfte behöver. */
  openingHours?: string | null
  description?: string | null
  address?: string | null
  phone?: string | null
  website?: string | null
  active: boolean
  sortOrder: number
  /** Underhålls av Cloud Functions – används i topplistan. */
  redemptionCount: number
  /** Skapat av "Skapa testhäfte" och raderas av "Rensa testdata". */
  isTest?: boolean
  createdAt: TimestampLike
  createdBy: string
  updatedAt?: TimestampLike
}

/* ---------- Erbjudandets sida i bläddringsläget ---------- */

/**
 * Utseendet på en enskild sida i häftet.
 *
 * I pappershäftet är klubbfärgerna omslaget medan kupongerna inuti är
 * företagens egna – en elegant svart serif hos den ena, grönrandigt hos den
 * andra. Det här låter samma sak hända digitalt.
 *
 * Valen är medvetet en styrd uppsättning i stället för fri redigering. Ett
 * företag ska kunna se ut som sig självt utan att någon kan råka göra sidan
 * oläslig, och alla kombinationer nedan har garanterad kontrast eftersom
 * textfärgen väljs separat och bilder alltid får ett mörkläggande raster.
 */
export type OfferPageBackground = 'lira' | 'company' | 'light' | 'dark' | 'color' | 'image'
export type OfferPageFont = 'display' | 'sans' | 'serif' | 'mono' | 'condensed'
export type OfferPageScale = 'sm' | 'md' | 'lg'
export type OfferPageTextTone = 'light' | 'dark'

export interface OfferPageStyle {
  background: OfferPageBackground
  /** Egen bakgrundsfärg som hex, t.ex. företagets profilfärg. */
  backgroundColor?: string | null
  backgroundImageUrl?: string | null
  /** Ersätter den automatiskt formulerade rabatttexten som sidans blickfång. */
  headline?: string | null
  font: OfferPageFont
  scale: OfferPageScale
  textTone: OfferPageTextTone
  /**
   * Hur stor del av sidan företagets design får.
   *
   * I pappershäftet ÄR kupongen hela sidan. Här delar den plats med rubrik,
   * värde och inlösenknapp, och standardläget ger designen ungefär övre
   * halvan. Ett företag med en färdig kupong vill komma närmare pappret.
   */
  coverage: OfferPageCoverage
  /**
   * Om en bakgrundsbild ska beskäras för att täcka, eller visas hel.
   *
   * Beskärning ser bäst ut på ett foto. En färdig kupong i stående format
   * blir däremot avklippt, och då vill man se den som företaget ritat den –
   * med profilfärgen som fond runtom.
   */
  imageFit: OfferPageImageFit
  /**
   * Designen bär redan rabatttexten.
   *
   * När ett företag levererat en färdig kupong står erbjudandet ofta redan i
   * bilden. Då dubblerar vår rubrik den. Med det här valet tystnar vår text
   * och deras kupong får tala själv – inlösenknappen ligger kvar, för den är
   * alltid vår.
   */
  designCarriesText: boolean
}

export type OfferPageCoverage = 'header' | 'half' | 'full'
export type OfferPageImageFit = 'cover' | 'contain'

export const OFFER_COVERAGE_LABELS: Record<OfferPageCoverage, string> = {
  header: 'Övre delen',
  half: 'Halva sidan',
  full: 'Hela sidan',
}

export const OFFER_IMAGE_FIT_LABELS: Record<OfferPageImageFit, string> = {
  cover: 'Beskär för att fylla',
  contain: 'Visa hela bilden',
}

export const DEFAULT_OFFER_PAGE_STYLE: OfferPageStyle = {
  background: 'lira',
  coverage: 'header',
  imageFit: 'cover',
  designCarriesText: false,
  backgroundColor: null,
  backgroundImageUrl: null,
  headline: null,
  font: 'display',
  scale: 'md',
  textTone: 'light',
}

/**
 * Är designen bara Lira-standarden, orörd?
 *
 * Formuläret sparade tidigare standarden på varje nytt erbjudande, även när
 * ingen rört ett enda val. Ett sådant erbjudande har aldrig aktivt valt en
 * egen design, och ska därför följa företagets profil precis som ett som
 * saknar design helt. Rubriken räknas inte – den är text, inte utseende.
 */
export function isDefaultPageStyle(style: OfferPageStyle | null | undefined): boolean {
  if (!style) return true
  const keys = Object.keys(DEFAULT_OFFER_PAGE_STYLE) as (keyof OfferPageStyle)[]
  return keys
    .filter((key) => key !== 'headline')
    .every((key) => (style[key] ?? null) === (DEFAULT_OFFER_PAGE_STYLE[key] ?? null))
}

export const OFFER_BACKGROUND_LABELS: Record<OfferPageBackground, string> = {
  lira: 'Lira – blått med gul text',
  company: 'Företagets egen färg',
  light: 'Ljus – vitt med svart text',
  dark: 'Mörk – nästan svart',
  color: 'Egen bakgrundsfärg',
  image: 'Egen bakgrundsbild',
}

/*
 * Bara systemfamiljer utöver klubbens två egna.
 *
 * Ett nytt webbtypsnitt kostar hundratals kilobyte som ska hämtas innan
 * texten syns, och häftet öppnas stående i en kassakö på mobildata. De här
 * finns redan på enheten och ritas direkt.
 */
export const OFFER_FONT_LABELS: Record<OfferPageFont, string> = {
  display: 'Montserrat – klubbens rubriktypsnitt',
  sans: 'Open Sans – neutral grotesk',
  serif: 'Serif – klassisk, som en tryckt annons',
  mono: 'Skrivmaskin – som en biljett eller kupong',
  condensed: 'Smal – mycket text på liten yta',
}

export const OFFER_SCALE_LABELS: Record<OfferPageScale, string> = {
  sm: 'Liten',
  md: 'Mellan',
  lg: 'Stor',
}

export const OFFER_TEXT_TONE_LABELS: Record<OfferPageTextTone, string> = {
  light: 'Ljus text',
  dark: 'Mörk text',
}

export interface Offer {
  id: string
  /** Erbjudanden hör till en period – varje nytt häfte har sitt innehåll. */
  bookletId: string
  companyId: string
  /** Denormaliserat för att slippa slå upp företaget i varje listrad. */
  companyName: string
  companyCategory: Category
  companyLogoUrl?: string | null
  /** Denormaliserad som loggan, av samma skäl: slippa slå upp företaget
   *  i varje listrad. Hålls i synk av syncCompanyToOffers. */
  companyBrandColor?: string | null
  /** Företagets grafiska profil, kopierad hit av samma skäl som färgen.
   *  Hålls i synk av syncCompanyToOffers. */
  companyPageStyle?: OfferPageStyle | null
  title: string
  description?: string | null
  /** Finstilt: "Gäller ej på redan nedsatta varor" o.d. Riktar sig till köparen. */
  terms?: string | null
  /**
   * Instruktion till butikspersonalen, t.ex. "Slå plu 2400 och ange rabatten".
   *
   * Står tryckt på flera av papperskupongerna i dag och är det enda kassören
   * behöver läsa. Visas därför på inlösenkvittot, inte bara i erbjudandet –
   * utan den blir den digitala versionen sämre än pappret för de företag som
   * har ett kassaflöde att följa.
   */
  staffInstructions?: string | null
  /**
   * Färdig kupongdesign från företaget.
   *
   * Många företag har redan en kupong framtagen och vill se sin egen i
   * häftet. Bilden blir då kupongens utseende rakt av.
   *
   * Vår inlösenknapp och stämpeln läggs alltid ovanpå, aldrig inbakade i
   * bilden. Den interaktiva delen får inte bero på en bild vi inte
   * kontrollerar – ett företag som levererar en bild med en ritad knapp på
   * skulle annars göra rabatten omöjlig att lösa in.
   */
  couponImageUrl?: string | null
  /**
   * Erbjudandets egen design, när den ska avvika från företagets profil.
   *
   * null betyder "följ företaget", och det är utgångsläget för nya
   * erbjudanden. Se resolveOfferStyle för i vilken ordning det avgörs.
   */
  pageStyle?: OfferPageStyle | null
  /**
   * Rubriken på erbjudandets sida, om den ska vara något annat än den
   * automatiska formuleringen av rabatten.
   *
   * Låg tidigare i pageStyle, men rubriken är erbjudandets text och inte
   * dess utseende – ett erbjudande som följer företagets profil ska ändå
   * kunna ha en egen rubrik. Äldre erbjudanden har den kvar i pageStyle,
   * och den läses därifrån när fältet här saknas.
   */
  headline?: string | null
  offerType: OfferType
  offerTerms: OfferTerms
  /** Schablonvärde i kronor – används för att räkna "sparat belopp". */
  estimatedValueSek: number
  active: boolean
  sortOrder: number
  redemptionCount: number
  /** Skapat av "Skapa testhäfte" och raderas av "Rensa testdata". */
  isTest?: boolean
  createdAt: TimestampLike
  createdBy: string
  updatedAt?: TimestampLike
}

/* ------------------------------------------------------------------ *
 * Inlösen
 * ------------------------------------------------------------------ */

export interface Redemption {
  /** Dokument-id är `${codeId}__${offerId}` – databasen garanterar därmed
   *  att ett erbjudande bara kan lösas in en gång per häfte. */
  id: string
  offerId: string
  offerTitle: string
  /** Sparas på posten så att statistiken per rabatttyp går att räkna ned
   *  igen om superadmin återställer inlösen. */
  offerType: OfferType
  companyId: string
  companyName: string
  bookletId: string
  codeId: string
  userUid: string
  /** Kort id som visas för butikspersonalen, t.ex. "K7M2Q9". */
  shortId: string
  valueSek: number
  redeemedAt: TimestampLike
  /**
   * Hela dygn mellan att häftet aktiverades och att rabatten användes.
   *
   * Räknas en gång, vid inlösen, i stället för att härledas i efterhand.
   * Statistiksidan behövde annars läsa in samtliga koder i perioden bara för
   * att veta när varje häfte aktiverades – tvåtusen dokument för att kunna
   * rita en stapel. Här kostar samma svar noll extra läsningar.
   *
   * Saknas den på äldre inlösen; grupperingen hoppar då över raden.
   */
  daysSinceActivation?: number | null
  /**
   * Inlösen hör till ett testhäfte.
   *
   * Styr bara en sak: föreningens globala statistik ska inte påverkas av
   * något som skett i ett provhäfte. Periodens egen statistik uppdateras som
   * vanligt, annars gick testhäftet inte att demonstrera.
   */
  isTest?: boolean
  /**
   * Räknarna är redan framskrivna av den som skapade posten.
   *
   * Sätts bara av testdatan, som skriver flera tusen inlösen på en gång och
   * räknar fram sina egna summor i samma svep. Utan flaggan skulle triggern
   * väckas lika många gånger i följd och komma fram till samma sak.
   *
   * Skild från isTest med flit. De två betydde länge samma sak i praktiken,
   * men de svarar på olika frågor – "hör det här till ett provhäfte?" och
   * "är summorna redan skrivna?" – och när räknarna flyttades till triggern
   * blev skillnaden avgörande: en riktig inlösen i ett testhäfte ska räknas,
   * en genererad ska inte.
   */
  aggregatesWritten?: boolean
  /** Superadmin kan återställa en felaktig inlösen. Posten raderas inte. */
  revoked: boolean
  revokedAt?: TimestampLike | null
  revokedBy?: string | null
  revokeReason?: string | null
}

/* ------------------------------------------------------------------ *
 * Användare
 * ------------------------------------------------------------------ */

export interface UserDoc {
  uid: string
  email: string
  role: Role
  /** Endast för lagledare. */
  teamId?: string | null
  /** Namn är frivilligt och efterfrågas bara av lagledare/admin. */
  displayName?: string | null
  createdAt: TimestampLike
  lastActiveAt: TimestampLike
  /** Sparat belopp per häfte, uppdateras vid varje inlösen. */
  savingsByBooklet: Record<string, { totalSek: number; redemptionCount: number }>
  /** Notiser användaren stängt, så de inte dyker upp igen. */
  dismissedNotices: string[]
  /** Sätts av GDPR-gallringen innan radering. */
  anonymizedAt?: TimestampLike | null
  /**
   * Kontot har varit stilla i två år och väntar på att föreningen tar ställning.
   *
   * Sätts och rensas bara av den veckovisa gallringen – reglerna låter inte
   * klienten röra fältet, annars hade den som flaggats kunnat flagga av sig
   * själv. Loggar personen in igen försvinner flaggan av sig själv.
   */
  inactiveFlaggedAt?: TimestampLike | null
}

/* ------------------------------------------------------------------ *
 * Loggning, rapporter och statistik
 * ------------------------------------------------------------------ */

export type AuditAction =
  | 'company.create'
  | 'company.update'
  | 'company.deactivate'
  | 'company.delete'
  | 'offer.create'
  | 'offer.update'
  | 'offer.deactivate'
  | 'offer.delete'
  | 'booklet.create'
  | 'booklet.update'
  | 'booklet.statusChange'
  | 'team.create'
  | 'team.update'
  | 'team.deactivate'
  | 'codes.generate'
  | 'codes.export'
  | 'stats.export'
  | 'loadtest.run'
  | 'feature.toggle'
  | 'code.markPaid'
  | 'code.markSold'
  | 'code.activate'
  | 'redemption.create'
  | 'redemption.revoke'
  | 'user.roleChange'
  | 'user.delete'
  | 'user.anonymize'
  | 'user.flagInactive'
  | 'report.statusChange'
  | 'contact.update'

export interface AuditEntry {
  id: string
  actorUid: string
  actorEmail: string
  actorRole: Role
  action: AuditAction
  targetType: string
  targetId: string
  /** Kort mening på svenska som visas rakt av i loggvyn. */
  summary: string
  details?: Record<string, unknown>
  at: TimestampLike
}

export type ReportType = 'bug' | 'idea'
export type ReportStatus = 'new' | 'inProgress' | 'done' | 'declined'

export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  new: 'Ny',
  inProgress: 'Pågår',
  done: 'Åtgärdad',
  declined: 'Avvisad',
}

export interface Report {
  id: string
  type: ReportType
  message: string
  page?: string | null
  userUid?: string | null
  userEmail?: string | null
  userAgent?: string | null
  status: ReportStatus
  adminNote?: string | null
  createdAt: TimestampLike
  updatedAt?: TimestampLike
}

/** Aggregat som admin-dashboarden läser. Id: "global" eller `booklet_<id>`. */
export interface StatsDoc {
  id: string
  bookletId?: string | null
  codesTotal: number
  codesSold: number
  codesActivated: number
  codesPaid: number
  redemptionCount: number
  totalSavingsSek: number
  /** Antal inlösen per rabatttyp. */
  redemptionsByType: Partial<Record<OfferType, number>>
  updatedAt: TimestampLike
}

/* ------------------------------------------------------------------ *
 * Hjälpfunktioner för perioder
 * ------------------------------------------------------------------ */

const STOCKHOLM_DATE = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Stockholm',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/**
 * Dagens datum i svensk tid, som "2026-10-20".
 *
 * Räknas uttryckligen i Europe/Stockholm, inte i den tidszon koden råkar
 * köra i. På en telefon i Sverige är det samma sak, men Cloud Functions kör
 * i UTC – och där var det fortfarande igår fram till klockan två på natten
 * sommartid. Servern nekade alltså inlösen under periodens första timmar,
 * medan telefonen visade häftet som öppet.
 */
export function stockholmDate(now: Date = new Date()): string {
  const parts = Object.fromEntries(
    STOCKHOLM_DATE.formatToParts(now).map((part) => [part.type, part.value]),
  )
  return `${parts.year}-${parts.month}-${parts.day}`
}

/** Antal hela dagar kvar till slutdatum, räknat från "idag" i svensk tid. */
export function daysUntil(isoDate: string, now: Date = new Date()): number {
  const [y, m, d] = isoDate.split('-').map(Number)
  const [ty, tm, td] = stockholmDate(now).split('-').map(Number)
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / 86_400_000)
}

/** Räknar ut vilken status ett häfte ska ha just nu. */
export function computeBookletStatus(
  booklet: Pick<Booklet, 'startDate' | 'endDate'>,
  now: Date = new Date(),
): BookletStatus {
  if (daysUntil(booklet.startDate, now) > 0) return 'upcoming'
  if (daysUntil(booklet.endDate, now) < 0) return 'expired'
  return 'active'
}

/**
 * Perioderna som fortfarande är i bruk – pågående och kommande, aldrig
 * utgångna.
 *
 * Ett häfte vars period tagit slut ska inte ligga kvar i häftesvyn med sina
 * erbjudanden. Rabatterna går ändå inte att lösa in, och en sida full av
 * sådana ser ut som om appen vore trasig snarare än som om säsongen vore
 * slut. Det gamla häftet finns kvar under Mitt konto, där det hör hemma:
 * som historik över vad man sparade.
 *
 * Statusen räknas ur datumen i stället för att läsas ur fältet i databasen.
 * Fältet uppdateras av ett nattligt jobb, och den som öppnar appen klockan
 * nio dagen efter att perioden tagit slut ska inte mötas av ett häfte som
 * råkar vara märkt som pågående ännu.
 */
export function usableBooklets<T extends Pick<Booklet, 'startDate' | 'endDate'>>(
  owned: T[],
  now: Date = new Date(),
): T[] {
  return owned.filter((booklet) => computeBookletStatus(booklet, now) !== 'expired')
}

/**
 * Den period vyerna utan egen väljare ska visa: en pågående om det finns en,
 * annars den senaste som ännu inte börjat. Finns ingetdera är svaret null,
 * och vyn visar samma tomma läge som för den som aldrig aktiverat något.
 */
export function currentBooklet<T extends Pick<Booklet, 'startDate' | 'endDate'>>(
  owned: T[],
  now: Date = new Date(),
): T | null {
  const usable = usableBooklets(owned, now)
  return usable.find((booklet) => computeBookletStatus(booklet, now) === 'active') ?? usable[0] ?? null
}

/** Genererar periodnamn i klubbens stil, t.ex. "Januari–juni 2027". */
export function suggestPeriodName(startDate: string, endDate: string): string {
  const months = [
    'januari', 'februari', 'mars', 'april', 'maj', 'juni',
    'juli', 'augusti', 'september', 'oktober', 'november', 'december',
  ]
  const [sy, sm] = startDate.split('-').map(Number)
  const [ey, em] = endDate.split('-').map(Number)
  const first = months[sm - 1]
  const capitalized = first.charAt(0).toUpperCase() + first.slice(1)
  return sy === ey
    ? `${capitalized}–${months[em - 1]} ${sy}`
    : `${capitalized} ${sy}–${months[em - 1]} ${ey}`
}

/* ------------------------------------------------------------------ *
 * Förberäknad statistik
 * ------------------------------------------------------------------ */

/**
 * Ett dygns inlösen för ett häfte, sammanräknat i förväg.
 *
 * Statistiksidan läste tidigare varje enskild inlösen – upp till femtusen
 * dokument, plus samtliga koder i perioden för att veta aktiveringsdatum.
 * Det fungerar vid några tusen inlösen och blir olidligt vid några tiotusen,
 * vilket är dit föreningen är på väg med fyratusen häften om året.
 *
 * Med ett dokument per dygn läser sidan i stället ett par hundra oavsett hur
 * mycket som hänt. Kostnaden växer med kalendern, inte med försäljningen.
 *
 * Dokument-id är `${bookletId}__${YYYY-MM-DD}` i svensk tid – samma dag som
 * användaren ser i gränssnittet, inte UTC-dygnet.
 */
export interface StatsDay {
  id: string
  bookletId: string
  /** ISO-datum i svensk tid, t.ex. "2026-08-18". */
  date: string
  count: number
  valueSek: number
  /**
   * Antal per timme i svensk tid, med timmen som nyckel: { "9": 3, "17": 8 }.
   *
   * En map och inte en array, därför att Firestore inte kan räkna upp ett
   * enskilt index i en array. Timmar utan inlösen saknas helt enkelt.
   */
  byHour: Record<string, number>
  byType: Partial<Record<OfferType, number>>
  byCompany: Record<string, number>
  /** Antal per spann av dagar efter aktivering, se SINCE_ACTIVATION_SPANS. */
  bySinceActivation: Record<string, number>
  updatedAt: TimestampLike
}

/**
 * Spannen för "dagar efter aktivering".
 *
 * Ligger i modellen och inte i statistiksidan, eftersom både uträkningen i
 * Cloud Functions och presentationen måste vara överens. Ändras gränserna på
 * ett ställe men inte det andra visar sidan staplar som inte stämmer med
 * någonting.
 */
export const SINCE_ACTIVATION_SPANS = [
  { key: 's0', label: 'Samma dag', max: 0 },
  { key: 's1', label: '1–7 dagar', max: 7 },
  { key: 's2', label: '8–30 dagar', max: 30 },
  { key: 's3', label: '31–60 dagar', max: 60 },
  { key: 's4', label: '61–90 dagar', max: 90 },
  { key: 's5', label: 'Efter 90 dagar', max: Infinity },
] as const

/** Vilket spann ett antal dagar hamnar i. */
export function sinceActivationSpan(days: number): string {
  return (SINCE_ACTIVATION_SPANS.find((span) => days <= span.max) ?? SINCE_ACTIVATION_SPANS[5]).key
}

/**
 * Datumnyckeln för ett dygn i svensk tid.
 *
 * Går via Intl i stället för toISOString, som ger UTC-dygnet. En inlösen
 * klockan 00:30 svensk sommartid hör hemma i dagen som just börjat, inte i
 * gårdagen – och det är gårdagen UTC skulle säga.
 */
export function swedishDateKey(date: Date): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/**
 * Klockslaget i svensk tid, "12:05".
 *
 * Samma skäl som för datumet: serverns klocka går i UTC, och en lunchrabatt
 * klockan tolv ska stå som tolv oavsett var den räknades fram.
 */
export function swedishTimeKey(date: Date): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date)
}

/*
 * Delarna av en tidpunkt i svensk tid.
 *
 * Fanns tidigare i tre kopior: en i företagsvyns uträkning, en i
 * Excel-exporten och en i CSV-filen – och CSV:n räknade dessutom i
 * webbläsarens egen tidszon. Samma inlösen kunde därför stå på olika
 * klockslag i två filer från samma knapprad.
 *
 * Engelska namn på veckodagarna är bara för att kunna tolka dem; de visas
 * aldrig.
 */
const SWEDISH_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Stockholm',
  year: 'numeric',
  month: 'numeric',
  weekday: 'short',
  hour: 'numeric',
  hourCycle: 'h23',
})

const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
}

export function swedishParts(ms: number): {
  year: number
  month: number
  /** Måndag är 0 – svensk vecka, inte amerikansk. */
  weekday: number
  hour: number
} {
  const parts: Record<string, string> = {}
  for (const part of SWEDISH_PARTS.formatToParts(new Date(ms))) parts[part.type] = part.value
  return {
    year: Number(parts.year),
    month: Number(parts.month) - 1,
    weekday: WEEKDAY_INDEX[parts.weekday] ?? 0,
    hour: Number(parts.hour) % 24,
  }
}

/** Timmen som spann, "12–13". */
export function hourSpanLabel(hour: number): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(hour)}–${pad((hour + 1) % 24)}`
}
